/**
 * Die Auswahl eines serverseitigen Enums — einmal für alle Fachbereiche.
 *
 * Vier Felder trugen dieselbe Bauform: Nutzungsart, Rechtsform, Zahlweise und
 * der Grund eines Konditionswechsels. Sie unterschieden sich in der
 * Beschriftung, im Feldnamen und in drei Schaltern — sonst Zeile für Zeile
 * gleich (gemessen von `check_duplication()`, 57–64 %).
 *
 * **Die Optionen kommen von außen, nicht von hier.** `fetch` steht
 * ausschließlich in der `api.ts` eines Fachbereichs (`apps/web/CLAUDE.md`); die
 * Abfrage bleibt deshalb im Fachbereich, der sie braucht. Diese Komponente
 * bekommt das Ergebnis als Prop — damit gilt weiter, was `ARCHITECTURE.md` 5
 * verlangt: Der Client pflegt keine eigene Zuordnung von Werten auf deutsche
 * Bezeichnungen.
 */

import { FIELD_CLASS } from '@/components/FormField'
import type { FieldError } from '@/components/FormField'

/** Ein `{value,label}`-Paar, wie es der Server für jedes Enum liefert. */
export interface EnumOption {
  readonly value: string
  readonly label: string
}

interface EnumSelectFieldProps {
  readonly label: string
  /** `data-testid` des Auswahlfelds selbst, z. B. `unit-create-usage-type`. */
  readonly testId: string
  /** Name der Eigenschaft in der API-Antwort, z. B. `usage_type`. */
  readonly apiField: string
  /** `<art>-create` oder `<art>-edit` — bestimmt mit `apiField` den
   * `data-testid` der Fehlermeldung. */
  readonly errorPrefix: string
  readonly options: readonly EnumOption[]
  /** Solange die Optionen laufen, ist das Feld gesperrt — sonst stünde dort
   * eine leere Auswahl, die wie „keine Möglichkeit" aussieht. */
  readonly isPending: boolean
  readonly value: string
  readonly onChange: (value: string) => void
  readonly error: FieldError | undefined
  readonly disabled?: boolean
  readonly required?: boolean
  /** Gesetzt, wenn es keine sinnvolle Vorauswahl gibt: zeigt einen gesperrten
   * Platzhalter, damit der leere Zustand nicht wie eine Wahl aussieht. */
  readonly placeholder?: string
}

export function EnumSelectField({
  label,
  testId,
  apiField,
  errorPrefix,
  options,
  isPending,
  value,
  onChange,
  error,
  disabled = false,
  required = false,
  placeholder,
}: EnumSelectFieldProps) {
  const hasError = error?.field === apiField
  const errorTestId = `${errorPrefix}-error-${apiField}`

  return (
    <label className="text-label text-muted flex flex-col gap-1" htmlFor={testId}>
      {label}
      <select
        id={testId}
        data-testid={testId}
        disabled={disabled || isPending}
        required={required}
        value={value}
        aria-invalid={hasError}
        aria-describedby={hasError ? errorTestId : undefined}
        onChange={(event) => {
          onChange(event.target.value)
        }}
        className={`text-text ${FIELD_CLASS}`}
      >
        {placeholder !== undefined && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {hasError && (
        <p id={errorTestId} data-testid={errorTestId} role="alert" className="text-status-overdue">
          {error.message}
        </p>
      )}
    </label>
  )
}
